/**
 * 세부 기능 권한(§14.19.2) — 서버 `FEATURE`(apps/server/src/common/constants.ts)와 **같은 값·같은 순서**.
 * 관리 화면(사용자 편집 체크)과 세션 창(없는 기능의 버튼 숨김)이 함께 쓴다.
 *
 * 🔴 화면에서 숨기는 것은 **편의**다. 강제는 에이전트가 한다(목록에 없는 기능의 채널을 만들지 않고 `rec` 을 거절).
 */
export const FEATURE = {
  CONTROL: 'control',
  CLIPBOARD: 'clipboard',
  FILE: 'file',
  CHAT: 'chat',
  RECORD: 'record',
  DRAW: 'draw',
} as const;

export type Feature = (typeof FEATURE)[keyof typeof FEATURE];

/** 기능 전체(표시·저장 순서). 새 사용자 기본값이자 관리자·코드 접속의 실효값. */
export const ALL_FEATURES: readonly Feature[] = Object.values(FEATURE);

/** 관리 화면 체크 목록의 이름·설명(순서 = ALL_FEATURES). */
export const FEATURE_OPTIONS: ReadonlyArray<{ value: Feature; label: string; hint: string }> = [
  { value: FEATURE.CONTROL, label: '원격 제어', hint: '마우스·키보드 입력(없으면 보기 전용)' },
  { value: FEATURE.CLIPBOARD, label: '클립보드', hint: '복사한 글을 서로 주고받기' },
  { value: FEATURE.FILE, label: '파일 전송', hint: '파일 보내기·받기' },
  { value: FEATURE.CHAT, label: '채팅', hint: '기기 앞 사람과 대화' },
  { value: FEATURE.RECORD, label: '녹화', hint: '세션 화면을 내 PC 에 녹화' },
  { value: FEATURE.DRAW, label: '그리기', hint: '상대 화면 위에 표시' },
];

export const FEATURE_LABEL: Readonly<Record<string, string>> = Object.fromEntries(
  FEATURE_OPTIONS.map((o) => [o.value, o.label]),
);

/** 기기 접근 수준이 "보기" 일 때 빠지는 기능 — 서버 `VIEW_BLOCKED_FEATURES` 와 같다(권한 패널 안내용). */
export const VIEW_BLOCKED_FEATURES: readonly Feature[] = [FEATURE.CONTROL, FEATURE.CLIPBOARD, FEATURE.FILE];

/**
 * 세션에서 이 기능을 써도 되는가 — 세션 화면·훅의 **유일한 판정 지점**.
 * @param granted 서버가 준 허용 목록(`Session.features`). `undefined` = 모름(옛 서버) → 허용(종전 동작 유지).
 */
export function featureAllowed(granted: readonly string[] | undefined, f: Feature): boolean {
  return !granted || granted.includes(f);
}

/** 서버가 준 목록을 정해진 순서로 정리(모르는 값은 버린다). */
export function normalizeFeatures(list: readonly string[]): Feature[] {
  const set = new Set(list);
  return ALL_FEATURES.filter((f) => set.has(f));
}

/**
 * 일반 사용자가 이 기기 수준에서 실제로 쓸 수 있는 기능 — 서버 `effectiveFeatures` 와 같은 규칙(안내용 미리보기).
 * @param level `view` | `control`(옛 `admin` 은 control 로 본다)
 */
export function featuresAtLevel(userFeatures: readonly string[], level: string): Feature[] {
  const own = normalizeFeatures(userFeatures);
  const atLevel = level === 'view' ? own.filter((f) => !VIEW_BLOCKED_FEATURES.includes(f)) : own;
  return withControlDependency(atLevel);
}

/**
 * 제어가 **전제**인 기능 — 클립보드·파일 전송은 원격에서 붙여넣기 단축키·놓기 클릭을 입력 주입으로 한다.
 * 서버 `CONTROL_DEPENDENT_FEATURES`·에이전트 `features.rs` 와 같은 규칙.
 */
export const CONTROL_DEPENDENT_FEATURES: readonly Feature[] = [FEATURE.CLIPBOARD, FEATURE.FILE];

/** 제어가 없으면 제어가 전제인 기능을 뺀다(체크 목록이 저장·표시 전에 쓴다). */
export function withControlDependency(list: readonly Feature[]): Feature[] {
  return list.includes(FEATURE.CONTROL) ? [...list] : list.filter((f) => !CONTROL_DEPENDENT_FEATURES.includes(f));
}

/** 목록을 사람이 읽는 한 줄로: 전부면 "전부", 없으면 "없음(보기만)". */
export function describeFeatures(list: readonly string[]): string {
  const n = normalizeFeatures(list);
  if (n.length === ALL_FEATURES.length) return '전부';
  if (n.length === 0) return '없음(보기만)';
  return n.map((f) => FEATURE_LABEL[f]).join(' · ');
}
